import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrowthShell from '../../components/growth/GrowthShell';
import RangeBar from '../../components/growth/GrowthRange';
import { ADLINK_AR, STATUS_AR, beginAdsOAuth, fetchAds, summarizeAds } from '../../lib/growthAds';
import { MIN_SAMPLE, rangeLabel, rangeWindow, readRange, spanLabel, writeRange } from '../../lib/growthRange';

// Growth OS «إدارة الحملات» — READ-ONLY: what each ad platform reports per
// campaign, beside what OUR attribution credits it with (sign-ups and paid
// orders by the UTM rule). Nothing on this page can change a campaign.

const LOW = 'بيانات قليلة';
const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d });
// Arabic order «945 ر.س» inside the LTR number cells.
const money = (n, cur) => <span dir="rtl">{fmt(n, 2)} {cur === 'SAR' || !cur ? 'ر.س' : cur}</span>;

function Ratio({ value, base, unit = '', d = 2 }) {
  if (value == null || !Number.isFinite(value)) return <span className="gos-faint">—</span>;
  const low = base < MIN_SAMPLE;
  return <span className={low ? 'gos-rate low' : 'gos-rate'} title={low ? `${LOW} (${fmt(base)}؛ الحد ${MIN_SAMPLE})` : undefined} data-low-sample={low ? '1' : '0'}><bdi dir="ltr">{fmt(value, d)}{unit}</bdi></span>;
}

// «ربط Snapchat»: mints the owner's one-time link and leaves for the platform's sign-in.
function ConnectButton({ p }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const go = async () => {
    setBusy(true); setFailed(false);
    try { window.location.assign(await beginAdsOAuth(p.key)); } catch (e) {
      console.warn('[GrowthCampaigns] link failed:', e?.message || e); setFailed(true); setBusy(false);
    }
  };
  return (
    <div className="gos-oauth">
      <button type="button" className={p.connected ? 'gos-btn ghost' : 'gos-btn'} onClick={go} disabled={busy} data-oauth={p.key}>
        {p.connected ? 'إعادة الربط' : `ربط ${p.ar}`}
      </button>
      <span className="gos-faint">سجّل الدخول بحساب العضو ذي صلاحية القراءة فقط. الرابط لمرة واحدة ولمدة 30 دقيقة.</span>
      {failed && <span style={{ color: 'var(--gos-bad)' }}>تعذّر إنشاء الرابط.</span>}
    </div>
  );
}

// Per ad (Meta): the same columns; our sign-ups and orders by utm_content = the ad ID.
function AdTable({ p }) {
  const cur = p.currency;
  const camp = Object.fromEntries(p.list.map((r) => [r.campaign_id, r.name]));
  return (
    <details className="gos-ads" data-ad-table={p.key} style={{ marginTop: 12 }}>
      <summary>الإعلانات ({fmt(p.adList.length)})</summary>
      <div className="gos-table-scroll">
        <table className="gos-table gos-table-wide">
          <thead><tr>
            <th>الإعلان</th><th>الحملة</th><th>الحالة</th><th>الإنفاق</th><th>الظهور</th><th>النقرات</th><th>CTR</th><th>CPC</th>
            <th>تحويلات المنصة</th><th>تسجيلات (لدينا)</th><th>طلبات مدفوعة (لدينا)</th><th>تكلفة الطلب</th>
          </tr></thead>
          <tbody>{p.adList.map((r) => (
            <tr key={r.ad_id} data-ad={r.ad_id}>
              <td><div>{r.name || '—'}</div><small className="gos-faint" dir="ltr">{r.ad_id}</small></td>
              <td>{camp[r.campaign_id] || <small className="gos-faint" dir="ltr">{r.campaign_id || '—'}</small>}</td>
              <td>{STATUS_AR[r.status] || r.status || '—'}</td>
              <td className="num">{money(r.spend, cur)}</td>
              <td className="num">{fmt(r.impressions)}</td>
              <td className="num">{fmt(r.clicks)}</td>
              <td className="num"><Ratio value={r.impressions ? (r.clicks / r.impressions) * 100 : null} base={r.impressions} unit="%" /></td>
              <td className="num"><Ratio value={r.clicks ? r.spend / r.clicks : null} base={r.clicks} /></td>
              <td className="num">{fmt(r.conversions, 1)}</td>
              <td className="num">{fmt(r.signups)}</td>
              <td className="num">{fmt(r.orders)}</td>
              <td className="num"><Ratio value={r.orders ? r.spend / r.orders : null} base={r.orders} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </details>
  );
}

function PlatformTable({ p }) {
  const cur = p.currency;
  return (
    <section className="gos-card" data-platform={p.key} style={{ marginBottom: 16 }}>
      <div className="gos-card-head">
        <h3>{p.ar}</h3>
        <span className={`gos-badge-soon${p.connected ? ' ok' : ''}`}>
          {p.later ? `لاحقاً — ${p.later}` : p.connected ? 'متصل · قراءة فقط' : 'غير متصل'}
        </span>
      </div>
      {p.connectedAt && <p className="gos-card-sub">مربوط منذ: {new Date(p.connectedAt).toLocaleString('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', dateStyle: 'medium', timeStyle: 'short' })}</p>}
      {p.lastPull && (
        <p className="gos-card-sub">آخر سحب: {new Date(p.lastPull.started_at).toLocaleString('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', dateStyle: 'medium', timeStyle: 'short' })}
          {p.lastPull.status === 'error' && <> — <span style={{ color: 'var(--gos-bad)' }}>فشل</span> <bdi dir="ltr" className="gos-faint">{String(p.lastPull.error || '').slice(0, 160)}</bdi></>}</p>
      )}
      {p.oauth && <ConnectButton p={p} />}
      {!p.connected ? (
        <div className="gos-empty-state">{p.later ? 'يُربط حين تُعتمد التطبيقات.' : 'لم تُضف بيانات هذه المنصة بعد.'}</div>
      ) : p.list.length === 0 ? (
        <div className="gos-empty-state"><strong>لا توجد حملات</strong></div>
      ) : (
        <div className="gos-table-scroll">
          <table className="gos-table gos-table-wide">
            <thead><tr>
              <th>الحملة</th><th>الحالة</th><th>الإنفاق</th><th>الظهور</th><th>النقرات</th><th>CTR</th><th>CPC</th>
              <th>تحويلات المنصة</th><th>تثبيتات</th><th>تسجيلات (لدينا)</th><th>طلبات مدفوعة (لدينا)</th><th>تكلفة التسجيل</th><th>تكلفة الطلب</th>
            </tr></thead>
            <tbody>{p.list.map((r) => (
              <tr key={r.campaign_id} data-campaign={r.campaign_id}>
                <td><div>{r.name || '—'}</div><small className="gos-faint" dir="ltr">{r.campaign_id}</small></td>
                <td>{STATUS_AR[r.status] || r.status || '—'}</td>
                <td className="num">{money(r.spend, cur)}</td>
                <td className="num">{fmt(r.impressions)}</td>
                <td className="num">{fmt(r.clicks)}</td>
                <td className="num"><Ratio value={r.impressions ? (r.clicks / r.impressions) * 100 : null} base={r.impressions} unit="%" /></td>
                <td className="num"><Ratio value={r.clicks ? r.spend / r.clicks : null} base={r.clicks} /></td>
                <td className="num">{fmt(r.conversions, 1)}</td>
                <td className="num">{fmt(r.installs, 1)}</td>
                <td className="num">{fmt(r.signups)}</td>
                <td className="num">{fmt(r.orders)}</td>
                <td className="num"><Ratio value={r.signups ? r.spend / r.signups : null} base={r.signups} /></td>
                <td className="num"><Ratio value={r.orders ? r.spend / r.orders : null} base={r.orders} /></td>
              </tr>
            ))}</tbody>
            <tfoot><tr>
              <td colSpan={2}><b>المجموع</b></td><td className="num"><b>{money(p.spend, cur)}</b></td><td colSpan={10} />
            </tr></tfoot>
          </table>
        </div>
      )}
      {p.adList.length > 0 && <AdTable p={p} />}
      {(p.unassigned.signups > 0 || p.unassigned.orders > 0) && (
        <p className="gos-note">من {p.ar} بلا رقم حملة في الرابط: {fmt(p.unassigned.signups)} تسجيل، {fmt(p.unassigned.orders)} طلب — الرابط لم يحمل utm_campaign بحسب القاعدة.</p>
      )}
    </section>
  );
}

export default function GrowthCampaigns({ user, profile, lang }) {
  const [params, setParams] = useSearchParams();
  const range = useMemo(() => readRange(params), [params]);
  const win = useMemo(() => rangeWindow(range), [range]);
  const touch = params.get('touch') === 'first' ? 'first_touch' : 'last_touch';
  const [raw, setRaw] = useState(null);
  const [err, setErr] = useState(false);
  const adlink = ADLINK_AR[params.get('adlink')] || null;

  useEffect(() => {
    let alive = true;
    setRaw(null); setErr(false);
    fetchAds(range, win).then((d) => { if (alive) setRaw(d); })
      .catch((e) => { console.warn('[GrowthCampaigns] load failed:', e?.message || e); if (alive) setErr(true); });
    return () => { alive = false; };
  }, [win.start, win.end]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => (raw ? summarizeAds(raw, touch) : null), [raw, touch]);
  const setTouch = (t) => { const p = new URLSearchParams(params); if (t === 'first') p.set('touch', 'first'); else p.delete('touch'); setParams(p); };

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="campaigns" title="إدارة الحملات"
                 actions={<span className="gos-top-range">{spanLabel(range.from, range.to)}</span>}>
      <div className="gos-hello">
        <h2>الحملات الإعلانية</h2>
        <p>{rangeLabel(range)}. قراءة فقط: لا شيء هنا يغيّر حملة. الإنفاق والظهور والنقرات من المنصة؛ التسجيلات والطلبات المدفوعة من نسبتنا نحن.</p>
      </div>
      <RangeBar range={range} onChange={(next) => { const p = writeRange(next); if (touch === 'first_touch') p.set('touch', 'first'); setParams(p); }} compare={false} />
      <div className="gos-range" role="group" aria-label="النسبة" style={{ marginBottom: 16 }}>
        <button type="button" className={touch === 'last_touch' ? 'on' : ''} onClick={() => setTouch('last')} data-touch="last">آخر مصدر قبل التسجيل/الطلب</button>
        <button type="button" className={touch === 'first_touch' ? 'on' : ''} onClick={() => setTouch('first')} data-touch="first">أول مصدر (30 يوماً)</button>
      </div>
      {adlink && <div className={`gos-adlink ${adlink.ok ? 'ok' : 'bad'}`} role="status" data-adlink={params.get('adlink')}>{adlink.text}</div>}
      {err && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state"><strong>تعذّر تحميل البيانات</strong>أعد تحميل الصفحة.</div></div>}
      {!data && !err && <div className="gos-card"><div className="gos-empty-state">…</div></div>}
      {data && ['google', 'meta', 'snapchat', 'tiktok'].map((k) => <PlatformTable key={k} p={data[k]} />)}
      <p className="gos-note">
        قاعدة الروابط: <bdi dir="ltr">utm_source</bdi> = اسم المنصة · <bdi dir="ltr">utm_campaign</bdi> = رقم الحملة (في Google يملؤه <bdi dir="ltr">{'{campaignid}'}</bdi> تلقائياً).
        في Meta: <bdi dir="ltr">{'utm_source=meta&utm_medium=paid_social&utm_campaign={{campaign.id}}&utm_content={{ad.id}}'}</bdi> — ويُنسب التسجيل والطلب للإعلان أيضاً. نقرات Meta هنا نقرات الرابط. تثبيتات iOS عبر SKAN تُعرض منفصلة على مستوى الحملة ولا تُوزَّع على المستخدمين.
        تكلفة التسجيل والطلب باهتة حتى {MIN_SAMPLE} تسجيلاً أو طلباً.
      </p>
    </GrowthShell>
  );
}
